// src/context/AuthContext.js
import { createContext, useContext, useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  sendEmailVerification,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp, onSnapshot, } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth, db } from "../config/firebase";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,              setUser]              = useState(null);
  const [profile,           setProfile]           = useState(null);
  const [isSuperAdmin,      setIsSuperAdmin]      = useState(false);
  const [superAdminChecked, setSuperAdminChecked] = useState(false);
  const [loading,           setLoading]           = useState(true);

  // ── Sign Up ──────────────────────────────────────────────────────────────
  // tenantId and role are optional — customers signing up from a tenant page
  // pass tenantId; admins are created via the onboarding flow directly.
  async function signup(name, email, password, tenantId = null, role = "customer") {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(cred.user, { displayName: name });

    await setDoc(doc(db, "users", cred.user.uid), {
      name,
      email,
      role,
      tenantId,
      createdAt: serverTimestamp(),
    });

    await sendEmailVerification(cred.user);
    return cred.user;
  }

  // ── Login ────────────────────────────────────────────────────────────────
  async function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  // ── Google OAuth ─────────────────────────────────────────────────────────
  // tenantId is optional — passed when signing in from a tenant booking page
  async function signInWithGoogle(tenantId = null) {
    const provider = new GoogleAuthProvider();
    const cred = await signInWithPopup(auth, provider);
    const { uid, displayName, email } = cred.user;

    const userRef = doc(db, "users", uid);
    const snap    = await getDoc(userRef);

    if (!snap.exists()) {
      // First Google sign-in — create the profile
      await setDoc(userRef, {
        name:      displayName || "",
        email:     email || "",
        role:      "customer",
        tenantId:  tenantId || null,
        createdAt: serverTimestamp(),
      });
      // Send welcome email — fire-and-forget, non-fatal.
      // Must use .catch() (not try/catch) — a non-awaited promise rejection
      // escapes try/catch and would surface as an unhandled rejection.
      httpsCallable(getFunctions(), "sendCustomerWelcomeEmail")({
        name:     displayName || "",
        email:    email || "",
        tenantId: tenantId || null,
      }).catch(() => { /* welcome email is non-fatal */ });
    } else if (tenantId && !snap.data().tenantId) {
      // Returning user signing in from a tenant page for the first time
      await setDoc(userRef, { tenantId }, { merge: true });
    }

    return cred;
  }

  // ── Logout ───────────────────────────────────────────────────────────────
  async function logout() {
    await signOut(auth);
    setProfile(null);
  }

  // ── Password Reset ───────────────────────────────────────────────────────
  async function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  // ── Resend verification email ────────────────────────────────────────────
  async function resendVerification() {
    if (auth.currentUser) await sendEmailVerification(auth.currentUser);
  }

  // ── Auth state listener ──────────────────────────────────────────────────
  useEffect(() => {
    let profileUnsub    = () => {};
    let superAdminUnsub = () => {};

    const unsub = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);

      // Clean up previous listeners
      profileUnsub();
      superAdminUnsub();

      if (firebaseUser) {
        // Real-time profile listener — picks up the user doc even if written
        // milliseconds after auth (e.g. during onboarding)
        profileUnsub = onSnapshot(
          doc(db, "users", firebaseUser.uid),
          (snap) => { setProfile(snap.exists() ? snap.data() : null); setLoading(false); },
          ()     => { setProfile(null); setLoading(false); },
        );

        superAdminUnsub = onSnapshot(
          doc(db, "superAdmins", firebaseUser.uid),
          (snap) => { setIsSuperAdmin(snap.exists()); setSuperAdminChecked(true); },
          ()     => { setIsSuperAdmin(false);         setSuperAdminChecked(true); },
        );
      } else {
        setProfile(null);
        setIsSuperAdmin(false);
        setSuperAdminChecked(true);
        setLoading(false);
      }
    });

    return () => { unsub(); profileUnsub(); superAdminUnsub(); };
  }, []);

  const value = {
    user,
    profile,
    loading,
    signup,
    login,
    signInWithGoogle,
    logout,
    resetPassword,
    resendVerification,
    isAdmin:          profile?.role === "admin",
    isStaff:          profile?.role === "staff",
    isSuperAdmin,
    superAdminChecked,
    emailVerified: user?.emailVerified ?? false,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
